import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import BottomNav from "../components/BottomNav";
import AddressSheet from "../components/AddressSheet";
import { useAppState } from "../context/AppState";
import { api } from "../lib/api";
import { useLocale } from "../context/Locale";
import StoriesStrip from "../components/StoriesStrip";
import PromoBanner, { normalizeSliderGroups } from "../components/PromoBanner";
import ThemeLoader from "../components/ThemeLoader";
import yapeLogo from "../assets/yape-logo.jpg";

const DELIVERY_HUARAZ_RED_ASSET = "/assets/themes/delivery-huaraz-red";
const FOOD_THEME_CACHE_KEY = "delivery-huaraz:food-theme:v2";
const FOOD_THEME_IDS = ["current", "visual_categories", "delivery_huaraz_red"];
const DELIVERY_HUARAZ_RED_DEFAULT_COLORS = {
  gradientStart: "#FF595B",
  gradientEnd: "#FF375B",
  navigationBackground: "#FFFFFF",
  navigationActive: "#D72547",
  navigationInactive: "#171717",
  browserBar: "#FF375B",
};
const DELIVERY_HUARAZ_RED_DEFAULT_BENEFITS = {
  enabled: true,
  allUrl: "/alerts",
  discountsUrl: "/alerts",
  deliveryUrl: "/services/point_to_point",
  rewardsUrl: "/wallet",
};

function readCachedThemeConfig() {
  try {
    const cached = JSON.parse(window.localStorage.getItem(FOOD_THEME_CACHE_KEY) || "null");
    if (!cached || !FOOD_THEME_IDS.includes(cached.theme)) return null;
    const colors = cached.colors || {};
    return {
      theme: cached.theme,
      loader: typeof cached.loader === "string" ? cached.loader : "",
      benefits: cached.benefits && typeof cached.benefits === "object"
        ? { ...DELIVERY_HUARAZ_RED_DEFAULT_BENEFITS, ...cached.benefits }
        : DELIVERY_HUARAZ_RED_DEFAULT_BENEFITS,
      colors: {
        gradientStart: validHexColor(colors.gradientStart, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.gradientStart),
        gradientEnd: validHexColor(colors.gradientEnd, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.gradientEnd),
        navigationBackground: validHexColor(colors.navigationBackground, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.navigationBackground),
        navigationActive: validHexColor(colors.navigationActive, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.navigationActive),
        navigationInactive: validHexColor(colors.navigationInactive, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.navigationInactive),
        browserBar: validHexColor(colors.browserBar, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.browserBar),
      },
    };
  } catch {
    return null;
  }
}

function validHexColor(value, fallback) {
  return /^#[0-9A-F]{6}$/i.test(String(value || "")) ? String(value).toUpperCase() : fallback;
}

function darkenHexColor(value, amount = 0.16) {
  const color = validHexColor(value, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.gradientEnd).slice(1);
  const channel = (offset) => Math.max(0, Math.round(parseInt(color.slice(offset, offset + 2), 16) * (1 - amount)));
  return `#${[0, 2, 4].map((offset) => channel(offset).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

const deliveryHuarazRedServiceIcons = {
  restaurants: `${DELIVERY_HUARAZ_RED_ASSET}/servicios/restaurantes.png`,
  taxi: `${DELIVERY_HUARAZ_RED_ASSET}/servicios/taxi.png`,
  delivery: `${DELIVERY_HUARAZ_RED_ASSET}/servicios/envios.png`,
  supermarket: `${DELIVERY_HUARAZ_RED_ASSET}/servicios/market.png`,
};

const deliveryHuarazRedCategoryIcons = {
  pollo: `${DELIVERY_HUARAZ_RED_ASSET}/categorias/pollos.png`,
  pizza: `${DELIVERY_HUARAZ_RED_ASSET}/categorias/pizzas.png`,
  alita: `${DELIVERY_HUARAZ_RED_ASSET}/categorias/alitas.png`,
  anticucho: `${DELIVERY_HUARAZ_RED_ASSET}/categorias/anticuchos.png`,
  oferta: `${DELIVERY_HUARAZ_RED_ASSET}/categorias/ofertas.png`,
  promocion: `${DELIVERY_HUARAZ_RED_ASSET}/categorias/ofertas.png`,
};

function normalizedLabel(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es");
}

function themedCategoryIcon(category, theme) {
  const configured = category?.theme_images?.[theme];
  if (configured) return configured;
  if (theme !== "delivery_huaraz_red") return category?.image;
  const normalized = normalizedLabel(category?.name);
  const match = Object.entries(deliveryHuarazRedCategoryIcons)
    .find(([keyword]) => normalized.includes(keyword));
  return match?.[1] || category?.image;
}

function BenefitLink({ to, children, ...props }) {
  return /^https?:\/\//i.test(String(to || ""))
    ? <a href={to} {...props}>{children}</a>
    : <Link to={to || "/"} {...props}>{children}</Link>;
}

function ThemeBenefits({ deliveryAvailable, config }) {
  const { t } = useLocale();
  const cards = [
    {
      to: config.discountsUrl,
      icon: `${DELIVERY_HUARAZ_RED_ASSET}/beneficios/descuentos.png`,
      title: t("exclusiveDiscounts"),
      copy: t("accordingToPromotions"),
    },
    {
      to: deliveryAvailable ? config.deliveryUrl : "/services",
      icon: `${DELIVERY_HUARAZ_RED_ASSET}/beneficios/envio-gratis.png`,
      title: t("deliveriesAndPickups"),
      copy: t("accordingToCoverage"),
    },
    {
      to: config.rewardsUrl,
      icon: `${DELIVERY_HUARAZ_RED_ASSET}/beneficios/acumula-puntos.png`,
      title: t("shoppingBenefits"),
      copy: t("moreNewsSoon"),
    },
  ];

  return (
    <section className="theme-benefits" aria-label={t("benefitsForYou")}>
      <header>
        <h2>{t("benefitsForYou")}</h2>
        <BenefitLink to={config.allUrl}>{t("seeAll")} <span aria-hidden="true">›</span></BenefitLink>
      </header>
      <div>
        {cards.map((card) => (
          <BenefitLink to={card.to} key={card.title}>
            <img src={card.icon} alt="" />
            <span><b>{card.title}</b><small>{card.copy}</small></span>
          </BenefitLink>
        ))}
      </div>
    </section>
  );
}

function normalizeRestaurant(restaurant) {
  const rawName = String(restaurant.name || "Negocio");
  const feeMatch = rawName.match(/\s*(🛵\s*S\/\s*\d+(?:[.,]\d+)?)\s*$/u);
  return {
    ...restaurant,
    cleanName: feeMatch ? rawName.slice(0, feeMatch.index).trim() : rawName,
    deliveryFee: feeMatch ? feeMatch[1].replace(/\s+/g, " ") : "",
  };
}

export default function FoodHome() {
  const { address, openAddress } = useAppState();
  const { t } = useLocale();
  const [params] = useSearchParams();
  const shouldOpenAddress = params.get("address") === "1";
  const [initialThemeConfig] = useState(() => readCachedThemeConfig());
  const [restaurants, setRestaurants] = useState([]);
  const [categorySlides, setCategorySlides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [homeTheme, setHomeTheme] = useState(initialThemeConfig?.theme || "");
  const [themeReady, setThemeReady] = useState(Boolean(initialThemeConfig));
  const [themeLoader, setThemeLoader] = useState(initialThemeConfig?.loader || "");
  const [deliveryHuarazRedColors, setDeliveryHuarazRedColors] = useState(
    initialThemeConfig?.colors || DELIVERY_HUARAZ_RED_DEFAULT_COLORS,
  );
  const [deliveryHuarazRedBenefits, setDeliveryHuarazRedBenefits] = useState(
    initialThemeConfig?.benefits || DELIVERY_HUARAZ_RED_DEFAULT_BENEFITS,
  );
  const searchInputRef = useRef(null);
  const [hubServices, setHubServices] = useState([]);
  const [serviceIcons, setServiceIcons] = useState({});
  const [additionalSections, setAdditionalSections] = useState([]);
  const [promoSliders, setPromoSliders] = useState(null);
  const [newStoresConfig, setNewStoresConfig] = useState({
    enabled: false,
    days: 60,
    limit: 6,
    selectedIds: [],
  });

  useEffect(() => {
    let active = true;
    api("/promo-slider", {
      method: "POST",
      body: JSON.stringify({
        latitude: address?.lat ?? null,
        longitude: address?.lng ?? null,
      }),
    })
      .then((data) => {
        if (active) setPromoSliders(data || {});
      })
      .catch(() => {
        if (active) setPromoSliders({});
      });
    return () => {
      active = false;
    };
  }, [address?.lat, address?.lng]);

  useEffect(() => {
    if (shouldOpenAddress) openAddress();
  }, [openAddress, shouldOpenAddress]);

  useEffect(() => {
    let active = true;
    api("/get-restaurant-category-slides", { method: "POST", body: "{}" })
      .then((data) => {
        if (active) setCategorySlides(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setCategorySlides([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    api("/service-hub")
      .then((data) => {
        if (!active) return;
        const nextTheme = FOOD_THEME_IDS.includes(data?.food_home?.theme) ? data.food_home.theme : "current";
        const redThemeOptions = data?.food_home?.theme_options?.delivery_huaraz_red || {};
        const nextColors = {
          gradientStart: validHexColor(redThemeOptions.gradient_start, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.gradientStart),
          gradientEnd: validHexColor(redThemeOptions.gradient_end, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.gradientEnd),
          navigationBackground: validHexColor(redThemeOptions.navigation_background, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.navigationBackground),
          navigationActive: validHexColor(redThemeOptions.navigation_active, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.navigationActive),
          navigationInactive: validHexColor(redThemeOptions.navigation_inactive, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.navigationInactive),
          browserBar: validHexColor(redThemeOptions.browser_bar, DELIVERY_HUARAZ_RED_DEFAULT_COLORS.browserBar),
        };
        const nextLoader = String(data?.food_home?.loaders?.[nextTheme] || "");
        const benefits = redThemeOptions?.benefits || {};
        const nextBenefits = {
          enabled: benefits.enabled !== false,
          allUrl: String(benefits.all_url || DELIVERY_HUARAZ_RED_DEFAULT_BENEFITS.allUrl),
          discountsUrl: String(benefits.discounts_url || DELIVERY_HUARAZ_RED_DEFAULT_BENEFITS.discountsUrl),
          deliveryUrl: String(benefits.delivery_url || DELIVERY_HUARAZ_RED_DEFAULT_BENEFITS.deliveryUrl),
          rewardsUrl: String(benefits.rewards_url || DELIVERY_HUARAZ_RED_DEFAULT_BENEFITS.rewardsUrl),
        };
        setHomeTheme(nextTheme);
        setDeliveryHuarazRedColors(nextColors);
        setDeliveryHuarazRedBenefits(nextBenefits);
        setThemeLoader(nextLoader);
        setThemeReady(true);
        try {
          window.localStorage.setItem(FOOD_THEME_CACHE_KEY, JSON.stringify({ theme: nextTheme, colors: nextColors, loader: nextLoader, benefits: nextBenefits }));
        } catch {
          // The page still works when storage is disabled.
        }
        setHubServices(Array.isArray(data?.services) ? data.services : []);
        setServiceIcons(data?.food_home?.icons && typeof data.food_home.icons === "object"
          ? data.food_home.icons
          : {});
        setAdditionalSections(Array.isArray(data?.food_home?.sections)
          ? data.food_home.sections
          : []);
        const config = data?.food_home?.new_stores;
        setNewStoresConfig({
          enabled: Boolean(config?.enabled),
          days: Math.max(1, Number(config?.days || 60)),
          limit: Math.max(1, Number(config?.limit || 6)),
          selectedIds: Array.isArray(config?.selected_ids)
            ? config.selected_ids.map(Number).filter(Number.isFinite)
            : [],
        });
      })
      .catch(() => {
        if (active) {
          if (!initialThemeConfig) setHomeTheme("current");
          setThemeReady(true);
          setNewStoresConfig((current) => ({ ...current, enabled: false }));
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (!themeColor) return;
    themeColor.setAttribute(
      "content",
      homeTheme === "delivery_huaraz_red" ? deliveryHuarazRedColors.browserBar : "#FFFFFF",
    );
  }, [deliveryHuarazRedColors.browserBar, homeTheme]);

  useEffect(() => {
    if (!address) {
      setRestaurants([]);
      return;
    }

    let active = true;
    setLoading(true);
    setError("");
    api("/get-delivery-restaurants", {
      method: "POST",
      body: JSON.stringify({
        latitude: Number(address.lat),
        longitude: Number(address.lng),
        accuracy: Math.max(0, Number(address.accuracy || 0)),
      }),
    })
      .then((data) => {
        if (active) setRestaurants((Array.isArray(data) ? data : []).map(normalizeRestaurant));
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [address]);

  const filteredRestaurants = useMemo(() => {
    const search = query.trim().toLocaleLowerCase("es");
    if (!search) return restaurants;
    return restaurants.filter((restaurant) =>
      `${restaurant.cleanName} ${restaurant.description || ""}`
        .toLocaleLowerCase("es")
        .includes(search),
    );
  }, [query, restaurants]);

  const promoSliderGroups = useMemo(
    () => normalizeSliderGroups(promoSliders),
    [promoSliders],
  );
  const mainPromoGroups = useMemo(
    () => promoSliderGroups.filter((group) => group.position === 0),
    [promoSliderGroups],
  );
  const secondaryPromoGroups = useMemo(
    () => promoSliderGroups.filter((group) => group.position > 0),
    [promoSliderGroups],
  );

  const newRestaurants = useMemo(() => {
    if (!newStoresConfig.enabled || query.trim()) return [];
    const cutoff = Date.now() - (newStoresConfig.days * 24 * 60 * 60 * 1000);
    if (newStoresConfig.selectedIds.length) {
      return restaurants
        .filter((restaurant) => newStoresConfig.selectedIds.includes(Number(restaurant.id)))
        .sort(
          (left, right) => newStoresConfig.selectedIds.indexOf(Number(left.id))
            - newStoresConfig.selectedIds.indexOf(Number(right.id)),
        )
        .slice(0, newStoresConfig.limit);
    }
    return restaurants
      .filter((restaurant) => {
        const joinedAt = Date.parse(restaurant.joined_at || "");
        return Number.isFinite(joinedAt) && joinedAt >= cutoff;
      })
      .sort((left, right) => Date.parse(right.joined_at) - Date.parse(left.joined_at))
      .slice(0, newStoresConfig.limit);
  }, [newStoresConfig, query, restaurants]);

  const taxiAvailable = Boolean(hubServices.find((service) => service.id === "TAXI")?.enabled);
  const deliveryAvailable = Boolean(hubServices.find((service) => service.id === "POINT_TO_POINT")?.enabled);
  const serviceIcon = (name) => homeTheme === "delivery_huaraz_red"
    ? (serviceIcons[name] || deliveryHuarazRedServiceIcons[name] || "")
    : (serviceIcons[name] || "");
  const themeStyle = homeTheme === "delivery_huaraz_red" ? {
    "--dh-gradient-start": deliveryHuarazRedColors.gradientStart,
    "--dh-gradient-end": deliveryHuarazRedColors.gradientEnd,
    "--dh-red": deliveryHuarazRedColors.gradientEnd,
    "--dh-red-dark": darkenHexColor(deliveryHuarazRedColors.gradientEnd),
    "--dh-nav-background": deliveryHuarazRedColors.navigationBackground,
    "--dh-nav-active": deliveryHuarazRedColors.navigationActive,
    "--dh-nav-inactive": deliveryHuarazRedColors.navigationInactive,
  } : undefined;

  if (!themeReady) {
    return <ThemeLoader src={themeLoader} label="Cargando apariencia" />;
  }

  return (
    <main className={`food-app food-home-theme-${homeTheme}`} style={themeStyle}>
      <AppHeader />
      <div className="food-app__content">
        <nav className="food-service-shortcuts" aria-label="Servicios de Delivery Huaraz">
          <Link className="food-service-shortcut is-active" to="/food" aria-current="page">
            <span aria-hidden="true">{serviceIcon("restaurants") ? <img src={serviceIcon("restaurants")} alt="" /> : "🍽️"}</span>
            <b>{t("restaurants")}</b>
          </Link>
          <Link className="food-service-shortcut food-service-shortcut--yape" to="/promos-yape">
            <span aria-hidden="true"><img src={yapeLogo} alt="" /></span>
            <b>Promos Yape</b>
          </Link>
          {taxiAvailable ? (
            <Link className="food-service-shortcut" to="/services/taxi">
              <span aria-hidden="true">{serviceIcon("taxi") ? <img src={serviceIcon("taxi")} alt="" /> : "🚕"}</span>
              <b>{t("taxiLabel")}</b>
            </Link>
          ) : (
            <span className="food-service-shortcut is-disabled" title={t("temporarilyUnavailable")}>
              <span aria-hidden="true">{serviceIcon("taxi") ? <img src={serviceIcon("taxi")} alt="" /> : "🚕"}</span>
              <b>{t("taxiLabel")}</b>
            </span>
          )}
          {deliveryAvailable ? (
            <Link className="food-service-shortcut" to="/services/point_to_point">
              <span aria-hidden="true">{serviceIcon("delivery") ? <img src={serviceIcon("delivery")} alt="" /> : "📦"}</span>
              <b>{t("shipments")}</b>
            </Link>
          ) : (
            <span className="food-service-shortcut is-disabled" title={t("temporarilyUnavailable")}>
              <span aria-hidden="true">{serviceIcon("delivery") ? <img src={serviceIcon("delivery")} alt="" /> : "📦"}</span>
              <b>{t("shipments")}</b>
            </span>
          )}
          <span className="food-service-shortcut is-disabled" title={t("soon")}>
            <span aria-hidden="true">{serviceIcon("supermarket") ? <img src={serviceIcon("supermarket")} alt="" /> : "🏪"}</span>
            <b>{homeTheme === "delivery_huaraz_red" ? "Market" : t("supermarket")}</b>
          </span>
          {additionalSections.map((section) => (
            <Link className="food-service-shortcut" to={section.destination_url} key={section.id}>
              <span aria-hidden="true">{section.image ? <img src={section.image} alt="" /> : "✨"}</span>
              <b>{section.name}</b>
            </Link>
          ))}
        </nav>
        {mainPromoGroups.map((group) => (
          <PromoBanner
            className="food-promo-banner"
            items={group.items}
            key={`promo-main-${group.id}`}
          />
        ))}
        <StoriesStrip />
        <section className="food-hero">
          <div>
            <small>DELIVERY HUARAZ</small>
            <h1>{t("foodQuestion")}</h1>
          </div>
          <Link to="/services" className="food-hero__services">{t("services")}</Link>
        </section>

        <div className="food-search-row">
          <div className="food-search">
            <button
              className="food-search__toggle"
              type="button"
              aria-label="Buscar"
              onClick={() => searchInputRef.current?.focus()}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.8" cy="10.8" r="6.8" /><path d="m16 16 4.2 4.2" /></svg>
            </button>
            <input
              ref={searchInputRef}
              type="search"
              placeholder={t("foodSearch")}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <Link to="/services" className="food-search-row__action" aria-label={t("services")}>
            <i /><i /><i />
          </Link>
        </div>

        {categorySlides.length > 0 && (
          <section className="general-categories" aria-label="Categorías generales">
            <div className="general-categories__heading">
              <div>
                <small>EXPLORA POR CATEGORÍA</small>
                <h2>¿Qué se te antoja?</h2>
              </div>
            </div>
            <div className="general-categories__track">
              {categorySlides.map((category) => (
                <Link
                  to={`/categories/stores?categories=${encodeURIComponent(
                    (category.categories_ids || []).map((item) => item.value).join(","),
                  )}&name=${encodeURIComponent(category.name)}&image=${encodeURIComponent(category.image || "")}`}
                  key={category.id}
                >
                  <span>
                    <img
                      src={themedCategoryIcon(category, homeTheme)}
                      alt=""
                      loading="lazy"
                    />
                  </span>
                  <b>{category.name}</b>
                </Link>
              ))}
            </div>
          </section>
        )}

        {homeTheme === "delivery_huaraz_red" && deliveryHuarazRedBenefits.enabled && (
          <ThemeBenefits deliveryAvailable={deliveryAvailable} config={deliveryHuarazRedBenefits} />
        )}

        {!address && (
          <section className="location-empty">
            <span>⌖</span>
            <h2>{t("chooseLocation")}</h2>
            <p>{t("chooseLocationHelp")}</p>
            <button type="button" onClick={openAddress}>{t("chooseAddress")}</button>
          </section>
        )}

        {address && (
          <>
          {newRestaurants.length > 0 && (
            <section className="new-stores-section" aria-label={t("newStores")}>
              <div className="new-stores-section__heading">
                <div>
                  <small>{t("justArrived")}</small>
                  <h2>{t("newStores")}</h2>
                </div>
                <span>{newRestaurants.length}</span>
              </div>
              <div className="new-stores-track">
                {newRestaurants.map((restaurant) => (
                  <Link
                    className="new-store-card"
                    to={`/food/stores/${restaurant.slug}`}
                    key={`new-${restaurant.id}`}
                  >
                    <div className="new-store-card__cover">
                      <img src={restaurant.cover_image || restaurant.image} alt="" loading="lazy" />
                      <span className={restaurant.is_active ? "is-open" : "is-closed"}>
                        {restaurant.is_active ? t("openNow") : t("closedNow")}
                      </span>
                    </div>
                    <div className="new-store-card__body">
                      <img src={restaurant.image} alt={restaurant.cleanName} loading="lazy" />
                      <div>
                        <h3>{restaurant.cleanName}</h3>
                        <b>★ {t("newLabel")}</b>
                        <p>◷ {restaurant.delivery_time || "20"} min {restaurant.deliveryFee ? ` · ${restaurant.deliveryFee}` : ""}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
          <section className="restaurant-section">
            <div className="restaurant-section__heading">
              <div>
                <small>{homeTheme === "delivery_huaraz_red" ? "NEGOCIOS DISPONIBLES" : t("nearYou")}</small>
                <h2>{loading
                  ? t("searchingStores")
                  : homeTheme === "delivery_huaraz_red"
                    ? "Cerca de ti"
                    : `${filteredRestaurants.length} ${t("storesAvailable")}`}</h2>
              </div>
              <button type="button" onClick={openAddress}>{t("changeZone")}</button>
            </div>

            {loading && (
              <div className="restaurant-list" aria-label="Cargando negocios">
                {[1, 2, 3].map((item) => <div className="restaurant-skeleton" key={item} />)}
              </div>
            )}

            {!loading && error && (
              <div className="food-message food-message--error">
                <b>No pudimos cargar los negocios.</b>
                <span>{error}</span>
              </div>
            )}

            {!loading && !error && filteredRestaurants.length === 0 && (
              <div className="food-message food-message--location">
                <b>{t("noStoresAtPoint")}</b>
                <span>{t("gpsOutsideCoverageHelp")}</span>
                <button type="button" onClick={openAddress}>{t("correctMapLocation")}</button>
              </div>
            )}

            {!loading && !error && filteredRestaurants.length > 0 && (
              <div className="restaurant-list">
                {filteredRestaurants.map((restaurant, index) => (
                  <Fragment key={restaurant.id}>
                  <Link
                    to={`/food/stores/${restaurant.slug}`}
                    className="restaurant-card"
                  >
                    <div className="restaurant-card__image">
                      <img
                        src={restaurant.image}
                        alt={restaurant.cleanName}
                        loading="lazy"
                      />
                      {!restaurant.is_active && <span>Cerrado</span>}
                    </div>
                    <div className="restaurant-card__body">
                      <div className="restaurant-card__title">
                        <h3>{restaurant.cleanName}</h3>
                        {restaurant.custom_featured_name && (
                          <em>{restaurant.custom_featured_name}</em>
                        )}
                      </div>
                      <p>{restaurant.description || "Conoce su carta y promociones."}</p>
                      <div className="restaurant-card__meta">
                        <span>★ {restaurant.avgRating || restaurant.rating || "5"}</span>
                        <span>◷ {restaurant.delivery_time || "20"} min</span>
                        {restaurant.deliveryFee && <b>{restaurant.deliveryFee}</b>}
                      </div>
                    </div>
                  </Link>
                  {secondaryPromoGroups
                    .filter((group) => group.position === index + 1)
                    .map((group) => (
                      <PromoBanner
                        className="food-promo-banner food-promo-banner--secondary"
                        items={group.items}
                        key={`promo-position-${group.position}-${group.id}`}
                        preserveAspectRatio
                      />
                    ))}
                  </Fragment>
                ))}
              </div>
            )}
          </section>
          </>
        )}
      </div>

      <BottomNav theme={homeTheme} />
      <AddressSheet />
    </main>
  );
}
